import { useState } from 'react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { CubeIcon } from '@heroicons/react/24/outline';
import { Button } from '../../tailwindComponents/Button';

const DISMISSED_STORAGE_KEY = 'floxProductSkuExplainerDismissed';

const readDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const storeDismissed = () => {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, '1');
  } catch {
    /* private mode — dismissal just won't persist */
  }
};

function UnitDotGrid({ count, columns }: { count: number; columns: number }) {
  return (
    <div
      className='grid gap-0.5'
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {Array.from({ length: count }, (_, dotIndex) => (
        <span
          key={dotIndex}
          className='h-1.5 w-1.5 rounded-[2px] bg-primary/70'
        />
      ))}
    </div>
  );
}

function SkuChip({
  count,
  columns,
  caption,
}: {
  count: number;
  columns: number;
  caption: string;
}) {
  return (
    <div className='flex flex-col items-center gap-1'>
      <div className='flex h-12 w-12 items-center justify-center rounded-lg border border-blue-200 bg-white shadow-sm'>
        <UnitDotGrid count={count} columns={columns} />
      </div>
      <span className='text-xs text-gray-500'>{caption}</span>
    </div>
  );
}

function StepArrow() {
  return (
    <div className='flex items-center justify-center md:self-center'>
      <ChevronRightIcon className='h-6 w-6 rotate-90 text-blue-300 md:rotate-0' />
    </div>
  );
}

export function ProductSkuExplainer() {
  const [isDismissed, setIsDismissed] = useState(readDismissed);

  if (isDismissed) return null;

  const dismiss = () => {
    storeDismissed();
    setIsDismissed(true);
  };

  return (
    <section className='rounded-lg border border-blue-100 bg-blue-50/60 p-5 font-inter'>
      <div className='flex items-start justify-between gap-3'>
        <h3 className='text-base font-semibold text-gray-900'>
          How products, SKUs and pallet stacking fit together
        </h3>
        <button
          type='button'
          onClick={dismiss}
          aria-label='Dismiss explainer'
          className='shrink-0 rounded-md p-1 text-gray-400 transition hover:bg-blue-100 hover:text-gray-600'
        >
          <XMarkIcon className='h-5 w-5' />
        </button>
      </div>
      <div className='mt-4 flex flex-col gap-4 md:flex-row md:items-stretch md:gap-6'>
        <div className='flex flex-1 flex-col items-start gap-2'>
          <div className='flex h-14 w-14 items-center justify-center rounded-lg border border-blue-200 bg-white shadow-sm'>
            <CubeIcon className='h-7 w-7 text-primary' />
          </div>
          <p className='text-sm font-semibold text-gray-900'>1. One product</p>
          <p className='text-sm text-gray-600'>
            The item you sell — set it up once, with its name and details.
          </p>
        </div>
        <StepArrow />
        <div className='flex flex-[1.4] flex-col items-start gap-2'>
          <div className='flex items-end gap-3' aria-hidden='true'>
            <SkuChip count={1} columns={1} caption='Single' />
            <SkuChip count={6} columns={3} caption='Pack of 6' />
            <SkuChip count={12} columns={4} caption='Pack of 12' />
          </div>
          <p className='text-sm font-semibold text-gray-900'>2. Its SKUs</p>
          <p className='text-sm text-gray-600'>
            Every size or pack you stock it in is its own SKU of that one
            product — sold singly, in packs of 6, packs of 12…
          </p>
        </div>
        <StepArrow />
        <div className='flex flex-1 flex-col items-start gap-2'>
          <div
            className='flex flex-col items-center gap-0.5'
            aria-hidden='true'
          >
            <UnitDotGrid count={12} columns={4} />
            <div className='h-1.5 w-12 rounded-sm bg-amber-600/60' />
            <div className='flex w-12 justify-between px-1'>
              <span className='h-1 w-1.5 bg-amber-600/60' />
              <span className='h-1 w-1.5 bg-amber-600/60' />
            </div>
          </div>
          <p className='text-sm font-semibold text-gray-900'>
            3. Pallet stacking
          </p>
          <p className='text-sm text-gray-600'>
            For each SKU, how many fit on a pallet — whether goods arrive or
            leave that way — so warehouses can store and move your stock.
          </p>
        </div>
      </div>
      <div className='mt-4'>
        <Button intent='secondary' size='sm' label='Got it' onClick={dismiss} />
      </div>
    </section>
  );
}
